import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Globe,
  Briefcase,
  Heart,
  Sun,
  MessageCircle,
  Smile,
  ArrowRight,
  ArrowLeft,
  Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE_OPTIONS = [
  { id: "professional", label: "Professional", desc: "Polished and authoritative", Icon: Briefcase },
  { id: "casual", label: "Casual & Friendly", desc: "Warm and approachable", Icon: Heart },
  { id: "inspirational", label: "Inspirational", desc: "Motivating and uplifting", Icon: Sun },
  { id: "conversational", label: "Conversational", desc: "Natural and relaxed", Icon: MessageCircle },
  { id: "humorous", label: "Humorous", desc: "Light-hearted and fun", Icon: Smile },
];

const LOADING_MESSAGES = [
  "Reading your website...",
  "Analyzing your brand voice...",
  "Crafting personalized prompts...",
  "Adding the human touch...",
  "Almost ready...",
];

const COLORS = ["#FF6B6B", "#FFD93D", "#4FACFE", "#A8E6CF", "#FFB347"];

const inputClass =
  "w-full bg-white border-2 border-[#E9ECEF] text-[#2D3436] placeholder-[#A0AEC0] rounded-2xl px-5 py-4 focus:outline-none focus:border-[#FF6B6B] focus:ring-4 focus:ring-[#FF6B6B]/10 transition-all text-base";

const QuestionFlow = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [tone, setTone] = useState("");
  const [openAnswer, setOpenAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("coc_lead");
    if (saved) {
      try {
        const lead = JSON.parse(saved);
        if (lead.websiteUrl) setWebsiteUrl(lead.websiteUrl);
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    if (!isLoading) return;
    const interval = setInterval(() => {
      setLoadingMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [isLoading]);

  const handleGenerate = useCallback(async () => {
    const saved = JSON.parse(localStorage.getItem("coc_lead") || "{}");
    setIsLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/generate`, {
        lead_id: saved.lead_id || "",
        website_url: websiteUrl,
        tone,
        open_answer: openAnswer,
        phone: saved.phone || "",
        business_name: saved.businessName || "",
        email: saved.email || "",
      });

      localStorage.setItem("coc_prompts", JSON.stringify(res.data));
      navigate("/prompts");
    } catch (err) {
      setError("Something went wrong generating your prompts. Please try again.");
      setIsLoading(false);
    }
  }, [websiteUrl, tone, openAnswer, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center px-4">
        <div className="text-center max-w-xs w-full">
          {/* Spinner */}
          <div className="w-24 h-24 mx-auto mb-7 relative">
            <div className="absolute inset-0 rounded-full border-4 border-[#FFE3E3] border-t-[#FF6B6B] animate-spin" />
            <div className="absolute inset-3 rounded-full bg-[#FFE3E3] flex items-center justify-center">
              <Sparkles className="text-[#FF6B6B]" size={26} />
            </div>
          </div>

          <h2
            style={{ fontFamily: "'Young Serif', Georgia, serif" }}
            className="text-3xl font-bold text-[#2D3436] mb-2"
          >
            Creating your prompts
          </h2>
          <p
            data-testid="loading-message"
            className="text-[#636E72] text-base transition-all duration-500"
          >
            {LOADING_MESSAGES[loadingMsgIdx]}
          </p>

          {/* Bounce dots */}
          <div className="mt-8 flex justify-center gap-2">
            {COLORS.map((color, i) => (
              <div
                key={i}
                className="w-3 h-3 rounded-full animate-bounce"
                style={{
                  backgroundColor: color,
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Progress bar */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all duration-400 ${
                  s === step
                    ? "w-8 bg-[#FF6B6B]"
                    : s < step
                    ? "w-4 bg-[#FF6B6B]/40"
                    : "w-4 bg-[#E9ECEF]"
                }`}
              />
            ))}
          </div>
          <span className="text-sm text-[#636E72]">Step {step} of 3</span>
        </div>

        {/* Step 1 — Website URL */}
        {step === 1 && (
          <div
            data-testid="question-step-1"
            className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-7 border border-[#F1F3F5] animate-fade-in-up"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#FFE3E3] flex items-center justify-center flex-shrink-0">
                <Globe className="text-[#FF6B6B]" size={18} />
              </div>
              <span className="text-sm font-semibold text-[#FF6B6B]">
                Step 1 — Your Website
              </span>
            </div>
            <h2
              style={{ fontFamily: "'Young Serif', Georgia, serif" }}
              className="text-3xl font-bold text-[#2D3436] mb-2 tracking-tight"
            >
              What's your business website?
            </h2>
            <p className="text-[#636E72] text-sm mb-6">
              Our AI reads your website to understand your brand voice and craft
              prompts that sound like <em>you</em> — not a template.
            </p>
            <input
              data-testid="question-website-url-input"
              type="text"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://yourbusiness.com"
              className={`${inputClass} mb-6`}
            />
            <button
              data-testid="question-step1-next-button"
              onClick={() => setStep(2)}
              disabled={!websiteUrl.trim()}
              className="w-full bg-[#FF6B6B] text-white font-bold rounded-full px-8 py-4 shadow-[0_8px_20px_rgba(255,107,107,0.3)] hover:shadow-[0_12px_25px_rgba(255,107,107,0.4)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base"
            >
              Continue <ArrowRight size={18} />
            </button>
            <p className="text-center text-xs text-[#636E72] mt-3">
              Don't have a website? Type your business name and we'll still create great prompts.
            </p>
          </div>
        )}

        {/* Step 2 — Tone Selector */}
        {step === 2 && (
          <div
            data-testid="question-step-2"
            className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-7 border border-[#F1F3F5] animate-fade-in-up"
          >
            <h2
              style={{ fontFamily: "'Young Serif', Georgia, serif" }}
              className="text-3xl font-bold text-[#2D3436] mb-2 tracking-tight"
            >
              How would you describe your brand's voice?
            </h2>
            <p className="text-[#636E72] text-sm mb-5">
              This helps us match the tone of your prompts to how you naturally communicate.
            </p>

            <div className="space-y-2.5 mb-6">
              {TONE_OPTIONS.map(({ id, label, desc, Icon }) => (
                <button
                  data-testid={`tone-option-${id}`}
                  key={id}
                  onClick={() => setTone(id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 text-left transition-all duration-200 ${
                    tone === id
                      ? "border-[#FF6B6B] bg-[#FFF5F5]"
                      : "border-[#E9ECEF] hover:border-[#FFB3B3] hover:bg-[#FFF9F9]"
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      tone === id ? "bg-[#FF6B6B]" : "bg-[#F1F3F5]"
                    }`}
                  >
                    <Icon
                      size={16}
                      className={tone === id ? "text-white" : "text-[#636E72]"}
                    />
                  </div>
                  <div className="flex-1">
                    <p
                      className={`font-semibold text-sm ${
                        tone === id ? "text-[#FF6B6B]" : "text-[#2D3436]"
                      }`}
                    >
                      {label}
                    </p>
                    <p className="text-xs text-[#636E72]">{desc}</p>
                  </div>
                  {tone === id && (
                    <div className="w-5 h-5 rounded-full bg-[#FF6B6B] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                data-testid="question-step2-back-button"
                onClick={() => setStep(1)}
                className="bg-white text-[#2D3436] border-2 border-[#E9ECEF] hover:border-[#FF6B6B] rounded-full px-5 py-4 font-semibold transition-all duration-300 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                data-testid="question-step2-next-button"
                onClick={() => setStep(3)}
                disabled={!tone}
                className="flex-1 bg-[#FF6B6B] text-white font-bold rounded-full px-8 py-4 shadow-[0_8px_20px_rgba(255,107,107,0.3)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Continue <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Open Question */}
        {step === 3 && (
          <div
            data-testid="question-step-3"
            className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-7 border border-[#F1F3F5] animate-fade-in-up"
          >
            <h2
              style={{ fontFamily: "'Young Serif', Georgia, serif" }}
              className="text-3xl font-bold text-[#2D3436] mb-2 tracking-tight"
            >
              One last thing...
            </h2>
            <p className="text-base text-[#2D3436] font-semibold mb-1">
              What's one thing you wish more people knew about your business?
            </p>
            <p className="text-sm text-[#636E72] mb-2">
              This is the human element that makes your prompts feel real. No
              wrong answer — just be yourself.
            </p>
            <p className="text-sm text-[#FF6B6B] font-medium mb-5">
              The more detail you share, the more personalized your prompts will be.
            </p>
            <textarea
              data-testid="question-open-answer-input"
              value={openAnswer}
              onChange={(e) => setOpenAnswer(e.target.value)}
              placeholder="E.g. 'We take every client through a whole process before the first session even starts, so by the time we meet, I already know their story...'"
              rows={4}
              className={`${inputClass} resize-none mb-5`}
            />

            {error && (
              <div
                data-testid="question-error"
                className="text-[#FF6B6B] text-sm bg-[#FFE3E3] rounded-2xl p-3 mb-4"
              >
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                data-testid="question-step3-back-button"
                onClick={() => setStep(2)}
                className="bg-white text-[#2D3436] border-2 border-[#E9ECEF] hover:border-[#FF6B6B] rounded-full px-5 py-4 font-semibold transition-all duration-300 flex items-center justify-center"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                data-testid="question-generate-button"
                onClick={handleGenerate}
                disabled={!openAnswer.trim()}
                className="flex-1 bg-[#FF6B6B] text-white font-bold rounded-full px-8 py-4 shadow-[0_8px_20px_rgba(255,107,107,0.3)] hover:shadow-[0_12px_25px_rgba(255,107,107,0.4)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base"
              >
                <Sparkles size={18} /> Create My Prompts
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionFlow;
